import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, throwError } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import {
  AppUser,
  AppUserAuth,
  Language,
} from '../../interfaces/auth/login.interfaces';
import { Globals } from '../globals/globals';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _userAuth$: BehaviorSubject<AppUserAuth | undefined>;
  readonly userAuth$: Observable<AppUserAuth | undefined>;

  get userAuth() {
    return this._userAuth$.value;
  }

  private set userAuth(userAuth: AppUserAuth) {
    this._userAuth$.next(userAuth);
    localStorage.setItem(
      this.globals.localStorageTokenName_luqas,
      JSON.stringify(userAuth),
    );
  }

  get isAuthenticated$(): Observable<boolean> {
    return this.userAuth$.pipe(map((userAuth) => userAuth !== undefined));
  }

  get isAuthenticated() {
    return this.userAuth !== undefined;
  }

  get userName$(): Observable<string> {
    return this.userAuth$.pipe(map((userAuth) => userAuth?.userName ?? ''));
  }

  get userLanguage$(): Observable<Language | undefined> {
    return this.userAuth$.pipe(
      map((userAuth) => userAuth?.language),
      distinctUntilChanged(),
    );
  }

  get token(): string {
    return this.userAuth?.accessToken ?? '';
  }

  constructor(
    private http: HttpClient,
    private globals: Globals,
  ) {
    const userAuthString: string | null = localStorage.getItem(
      this.globals.localStorageTokenName_luqas,
    );
    const userAuth: AppUserAuth | undefined =
      userAuthString === null ? undefined : JSON.parse(userAuthString);

    if (userAuth) {
      this.refreshToken();
    }

    this._userAuth$ = new BehaviorSubject<AppUserAuth | undefined>(userAuth);
    this.userAuth$ = this._userAuth$.asObservable();
  }

  loginWithQbusCloud() {
    const json = localStorage.getItem(this.globals.localStorageTokenName_app);
    if (json) {
      const currentUser = JSON.parse(json);
      this.userAuth = currentUser;
    }
  }

  login(entity: AppUser): Promise<AppUserAuth> {
    const url = `${this.globals}/jwtsecurity/login`;
    return this.loginWithPath(entity, url);
  }

  loginMqtt(entity: AppUser): Promise<AppUserAuth> {
    const url = `${this.globals.apiRoot}/jwtsecurity/loginmqtt`;
    return this.loginWithPath(entity, url);
  }

  async loginWithPath(entity: AppUser, path: string): Promise<AppUserAuth> {
    const userAuth = await lastValueFrom(
      this.http.post<AppUserAuth>(path, entity, this.globals.httpOptions),
    );

    if (userAuth) this.userAuth = userAuth;

    return userAuth;
  }

  async autoLogin(id: number, password: string): Promise<boolean> {
    const url = `${this.globals.apiRoot}/semsaWizardAutologin/${id}`;
    try {
      const userAuth = await lastValueFrom(
        this.http.post<AppUserAuth>(url, { password }),
      );

      if (userAuth) {
        this.userAuth = userAuth;
        return true;
      }
    } catch (err) {
      console.error(err);
      return false;
    }

    return false;
  }

  logout(redirectToLogin: boolean = true): void {
    localStorage.removeItem(this.globals.localStorageTokenName_luqas);
    this._userAuth$.next(undefined);
  }

  refreshToken(): Observable<AppUserAuth | null> {
    const currentUser = JSON.parse(
      localStorage.getItem(this.globals.localStorageTokenName_luqas),
    );

    if (currentUser) {
      const token = currentUser.refreshToken;
      const url = `${this.globals.apiRoot}/jwtsecurity/refresh`;

      return this.http.post<AppUserAuth>(url, { token: token }).pipe(
        tap((resp) => {
          // Use object assign to update the current object
          // NOTE: Don't create a new AppUserAuth object
          //       because that destroys all references to object
          Object.assign(this.userAuth, resp);

          // Store into local storage
          localStorage.setItem(
            this.globals.localStorageTokenName_luqas,
            JSON.stringify(this.userAuth),
          );
        }),
      );
    }

    return throwError(new Error('User is logged out.'));
  }
}
